import {
	Button,
	Drawer,
	FilterIcon,
	RadioGroup,
	RadioGroupItem,
	SortIcon,
} from "@au-schein-de/au-ui-library";
import { getProductsFilterSchema } from "@dransay/api/models";
import { zodResolver } from "@hookform/resolvers/zod";
import isNil from "lodash/isNil";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useFetcher } from "react-router";
import { useCart } from "~/features/cart/cart.context";
import { useDebouncedCallback } from "~/hooks/use-debounced-callback.hook";
import { useDeepCompareEffect } from "~/hooks/use-deep-compare-effect";
import { FILTER_REQUEST_DEBOUNCE_MS } from "./filters/constants";
import { ProductFilters } from "./filters/product-filters.component";
import { ProductFilters as ProductFiltersV1 } from "./filters/product-filters-v1.component";
import type { FacetCounts, FiltersConfig, FiltersState } from "./filters/types";
import { useFilters } from "./filters/use-filters.hook";
import { getActiveFilterGroupsCount } from "./filters/utils";
import { useProductSort } from "./product-list.use-product-sort.hook";

type FiltersDrawerProps = {
	filters: FiltersState;
	config: FiltersConfig;
	onFilterChange: (filters: FiltersState, clearedAllFilters: boolean) => void;
	useFiltersV2: boolean;
	facets?: FacetCounts;
	onTopProducerWarning?: () => void;
};

export function FiltersDrawer({
	filters,
	config,
	onFilterChange,
	useFiltersV2,
	facets,
	onTopProducerWarning,
}: FiltersDrawerProps) {
	const { t } = useTranslation("product");
	const { options, sort, onSortChange } = useProductSort();
	const [localSortSelection, setLocalSortSelection] = useState(sort);
	const { cartDisclosure } = useCart();

	const form = useForm({
		defaultValues: filters,
		resolver: zodResolver(getProductsFilterSchema()),
	});

	const clearedAllFilters = useRef(false);

	const {
		filters: localFilters,
		setFilter: setLocalFilter,
		setBulkFilters: setBulkLocalFilters,
		clearAllFilters: clearLocalFilters,
	} = useFilters(form);

	const onFilterDrawerOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				return form.reset();
			}

			setBulkLocalFilters(filters);
		},
		[form, filters, setBulkLocalFilters],
	);

	const activeFilterGroups = getActiveFilterGroupsCount(filters);
	const activeLocalFilterGroups = getActiveFilterGroupsCount(localFilters);

	const resetAndSort = useCallback(() => {
		if (!localSortSelection) return;
		onSortChange(localSortSelection);
		setLocalSortSelection(undefined);
	}, [localSortSelection, onSortChange]);

	const handleApplyFilters = useCallback(() => {
		const removedTopProducerFilter =
			!!filters.topProducersLowPrice && !localFilters.topProducersLowPrice;

		onFilterChange(localFilters, clearedAllFilters.current);

		if (removedTopProducerFilter) {
			onTopProducerWarning?.();
		}

		clearedAllFilters.current = false;
	}, [
		filters.topProducersLowPrice,
		localFilters,
		onFilterChange,
		onTopProducerWarning,
	]);

	const fetcher = useFetcher();

	// Skip the initial fetch — the drawer's facets / count come from the
	// SSR-provided `facets` prop on first paint. Refetching on mount used
	// to fire twice (because `localFilters` briefly oscillates during
	// hydration) and produced two redundant /api/products requests with
	// no observable change. Only fetch once the user actually toggles a
	// filter inside the drawer.
	const isInitialDrawerFetch = useRef(true);
	// Snapshot of the `localFilters` that produced the latest `fetcher.load`
	// call. We only trust `fetcher.data` when it still matches the current
	// snapshot; otherwise (same-route nav, back/forward, prop reset) we fall
	// back to the fresh SSR `facets` prop instead of flashing stale data.
	const lastRequestedFiltersRef = useRef<string | null>(null);
	const currentFiltersSnapshot = JSON.stringify(localFilters);
	const loadCount = useDebouncedCallback(
		(url: string, filtersSnapshot: string) => {
			lastRequestedFiltersRef.current = filtersSnapshot;
			fetcher.load(url);
		},
		{ delay: FILTER_REQUEST_DEBOUNCE_MS, leading: false },
	);
	useDeepCompareEffect(() => {
		if (isInitialDrawerFetch.current) {
			isInitialDrawerFetch.current = false;
			return;
		}
		// load products' count
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.set("filters", JSON.stringify(localFilters));
		searchParams.set("page", "0");
		// set pageSize to 1 to get the count only, and reducing the items to the minimum
		searchParams.set("pageSize", "1");
		loadCount(
			`/api/products?${searchParams.toString()}`,
			currentFiltersSnapshot,
		);
	}, [localFilters]);

	const fetcherMatchesCurrentFilters =
		fetcher.state === "idle" &&
		lastRequestedFiltersRef.current === currentFiltersSnapshot;
	const drawerFacets = fetcherMatchesCurrentFilters
		? (fetcher.data?.facets ?? facets)
		: facets;
	const drawerCount = fetcherMatchesCurrentFilters
		? fetcher.data?.count
		: undefined;

	if (cartDisclosure.isOpen) {
		return null;
	}

	return (
		<div className="md:hidden fixed bg-white bottom-0 left-0 right-0 border-t-2 border-gray-300 flex justify-around px-3 py-2.5 z-50">
			<Drawer.Root onOpenChange={onFilterDrawerOpenChange}>
				<Drawer.Trigger autoFocus={false} data-testid="filter-products">
					<span className="flex gap-2 h-12 items-center text-body-bold text-gray-700">
						<FilterIcon size={6} />
						{t("filters")}
						{activeFilterGroups > 0 && (
							<span className="text-body text-gray-500">
								({activeFilterGroups})
							</span>
						)}
					</span>
				</Drawer.Trigger>
				<Drawer.Content className="max-h-full" aria-describedby={undefined}>
					<Drawer.Header className="flex justify-between items-center">
						<div className="flex items-center gap-2">
							<Drawer.Title className="text-gray-700">
								{t("filters")}{" "}
								<span className="text-body">
									{activeLocalFilterGroups > 0 &&
										`(${activeLocalFilterGroups})`}
								</span>
							</Drawer.Title>
						</div>
						<div className="flex items-center gap-4">
							{activeLocalFilterGroups > 0 && (
								<Button
									variant="link"
									onClick={() => {
										clearedAllFilters.current = true;
										clearLocalFilters();
									}}
								>
									{t("filters.clearAll")}
								</Button>
							)}
							<Drawer.CloseIcon />
						</div>
					</Drawer.Header>

					<div className="overflow-y-auto px-6">
						{useFiltersV2 ? (
							<ProductFilters
								filters={localFilters}
								config={config}
								onFilterChange={setLocalFilter}
								facets={drawerFacets}
							/>
						) : (
							<ProductFiltersV1
								filters={localFilters}
								config={config}
								onFilterChange={setLocalFilter}
							/>
						)}
					</div>

					<Drawer.Footer>
						<Drawer.Close asChild>
							<Button
								data-testid="apply-filters"
								onClick={handleApplyFilters}
								pending={fetcher.state !== "idle"}
							>
								{t("filters.apply")}{" "}
								{!isNil(drawerCount) && (
									<span className="text-body">({drawerCount})</span>
								)}
							</Button>
						</Drawer.Close>
					</Drawer.Footer>
				</Drawer.Content>
			</Drawer.Root>

			<Drawer.Root onOpenChange={() => setLocalSortSelection(sort)}>
				<Drawer.Trigger autoFocus={false} data-testid="sort-products-by">
					<span className="flex gap-2 h-12 items-center text-body-bold text-gray-700">
						<SortIcon size={6} />
						{t(sort ?? "sort.recommended")}
					</span>
				</Drawer.Trigger>
				<Drawer.Content className="max-h-full" aria-describedby={undefined}>
					<Drawer.Header>
						<Drawer.Title>{t("sort.sortBy")}</Drawer.Title>
						<Drawer.CloseIcon />
					</Drawer.Header>

					<RadioGroup
						className="flex flex-col px-6"
						value={localSortSelection}
						onValueChange={setLocalSortSelection}
					>
						{options.map(({ key }) => (
							<RadioGroupItem
								key={key}
								value={key}
								size="sm"
								className="w-full bg-white"
								data-testid={key}
							>
								{t(key)}
							</RadioGroupItem>
						))}
					</RadioGroup>

					<Drawer.Footer>
						<Drawer.Close asChild data-testid="sort-products-by-confirm">
							<Button onClick={resetAndSort}>{t("sort.confirm")}</Button>
						</Drawer.Close>
					</Drawer.Footer>
				</Drawer.Content>
			</Drawer.Root>
		</div>
	);
}
